import React from 'react'
import {
  arrayOf,
  array,
  number,
  shape,
  string,
  oneOf,
  object,
  bool
} from 'prop-types'
import {
  ButtonGroup,
  Highlight,
  SpeakerThumbnail
} from '@/component/Primitive/Events'
import styles from './AgendaItemBlock.module.scss'
import ResponsiveImage from '@/component/Primitive/ResponsiveImage'
import cx from 'classnames'
import Stack from '@/component/Primitive/Stack'
import Hide from '@/component/Primitive/Hide'
import Type from '@/component/Primitive/Type'
import Icon from '@/component/Primitive/Icon'
import Container from '@/component/Primitive/Container'
import moment from 'moment'
import Prose from '@/component/Primitive/Prose'

const AgendaItemBlock = ({
  tag,
  title,
  subtitle,
  description,
  image,
  theme,
  inverse,
  logo,
  agendaItem,
  buttonGroup,
  timeRange
}) => {
  const { mobile: mobileImage, desktop: desktopImage } = image || {}
  const { mobile: mobileLogo, desktop: deskotpLogo } = logo || {}

  const { location, startDate, speakers } = agendaItem
  // Need agenda start and end date, location name & speakers, and register action

  // Changed format to use HH:mm for 24-hour time
  const formattedStartDate = moment(startDate).format('MMM D, YYYY')
  const dateRange = `${formattedStartDate} | ${timeRange}`

  return (
    <div
      className={cx(
        styles.AgendaItemBlock,
        theme === 'dark' && styles.dark,
        theme === 'grey' && styles.grey
      )}
    >
      <Container
        className={cx(styles.AgendaItemBlockInner, inverse && styles.inverse)}
        size="wide"
        center
      >
        <div className={styles.ImagesWrapper}>
          {desktopImage && (
            <Image device="desktop" image={desktopImage} logo={deskotpLogo} />
          )}
          {mobileImage && (
            <Image device="mobile" image={mobileImage} logo={mobileLogo} />
          )}
        </div>
        <div className={styles.ContentWrapper}>
          <div className={styles.Content}>
            <Stack gap="medium">
              <Stack gap="small">
                {subtitle && (
                  <Type
                    as="h2"
                    size={['body3', 'body3']}
                    weight="regular"
                    className={styles.Subtitle}
                  >
                    {subtitle}
                  </Type>
                )}
                <Stack gap="tiny">
                  {title && (
                    <Type
                      as="h1"
                      size={['heading5', 'heading4']}
                      weight="medium"
                      className={styles.Title}
                    >
                      <Highlight>{title}</Highlight>
                    </Type>
                  )}
                  <div className={styles.InfoRow}>
                    <Type
                      as="p"
                      weight="medium"
                      size={['body4', 'body4']}
                      className={styles.DateRange}
                    >
                      <Icon
                        width="20"
                        type="calendar"
                        className={styles.Calendar}
                      />
                      {dateRange}
                    </Type>
                    {location && location?.name && (
                      <Type
                        as="p"
                        weight="medium"
                        size={['body4', 'body4']}
                        className={styles.Location}
                      >
                        <Icon
                          width="14"
                          type="map-pin"
                          className={styles.Pin}
                        />
                        {location.name}
                      </Type>
                    )}
                  </div>
                </Stack>
              </Stack>
              {!!speakers.length && (
                <Stack gap="tiny">
                  <Type
                    as="h1"
                    size={['body3', 'body3']}
                    weight="bold"
                    className={styles.SpeakersTitle}
                  >
                    Speakers
                  </Type>
                  <div className={styles.Speakers}>
                    {speakers.map((speaker, i) => (
                      <SpeakerThumbnail key={i} {...speaker} />
                    ))}
                  </div>
                </Stack>
              )}
              {description && <Prose dangerousHtml={description} />}
              <div className={styles.Buttons}>
                {!!buttonGroup?.length && (
                  <ButtonGroup group={buttonGroup} theme={theme} />
                )}
              </div>
            </Stack>
          </div>
        </div>
      </Container>
    </div>
  )
}

AgendaItemBlock.propTypes = {
  tag: string,
  title: string,
  subtitle: string,
  description: string,
  theme: string,
  ctaLabel: string,
  buttonGroup: array,
  fullUrlPath: string,
  inverse: bool,
  timezone: string,
  timeRange: string,
  registerButtonClassName: string,
  registerAction: object,
  agendaItem: shape({
    location: shape({
      name: string
    }),
    startDate: string,
    endDate: string,
    speakers: array
  }),
  image: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  }),
  logo: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  })
}

const Image = ({ device, image, logo }) => {
  const hideProps =
    device === 'mobile' ? { at: 'desktop' } : { below: 'desktop' }
  const className =
    device === 'mobile' ? styles.mobileImage : styles.desktopImage
  return (
    <Hide {...hideProps} className={styles.ImageWrapper}>
      <ResponsiveImage className={cx(styles.Image, className)} {...image} />
      {logo && (
        <div className={styles.LogoWrapper}>
          <Type
            as="span"
            weight="medium"
            size={['body3', 'body2']}
            className={styles.LogoText}
          >
            In association with
          </Type>
          <ResponsiveImage className={cx(styles.Logo, className)} {...logo} />
        </div>
      )}
    </Hide>
  )
}

Image.propTypes = {
  device: oneOf(['mobile', 'desktop']),
  image: object,
  logo: object
}

export default AgendaItemBlock
